import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, BarChart3, Database, Link2, Users } from 'lucide-react';

import { useAdminIdentityAttention, useAdminIdentityAttentionMutations } from '../../hooks/useApi';
import type {
    AdminIdentityAttentionAuth0EmailConflict,
    AdminIdentitySyncStats,
} from '../../services/api';
import { AdminAuth0BulkSyncHub } from './AdminAuth0BulkSyncHub';
import { cn, pluralize } from '../../lib/utils';
import { LoadingState } from '../LoadingSpinner';
import { AdminDashboardPaginatedSection } from './AdminDashboardPrimitives';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { InlineAlert, type InlineAlertTone } from '../ui/inline-alert';
import { SearchField } from '../ui/search-field';
import { SegmentedTab } from '../ui/segmented-control';
import { segmentedTabListClassName } from '../ui/segmented-styles';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { pageTitleClassName, sectionTitleClassName } from '../ui/typography';
import { TabList } from '../ui/tabs';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const PAGE_SIZE = 25;
type AttentionTab =
    | 'sync-progress'
    | 'users-without-teams'
    | 'auth0-missing-db'
    | 'auth0-email-conflicts';

type StatusMessage = {
    tone: InlineAlertTone;
    text: string;
};

function toStatusMessage(
    success: boolean,
    successText: string,
    pendingText: string,
): StatusMessage {
    return {
        tone: success ? 'info' : 'warning',
        text: success ? successText : pendingText,
    };
}

function toErrorStatusMessage(error: unknown, fallbackText: string): StatusMessage {
    return {
        tone: 'error',
        text: error instanceof Error ? error.message : fallbackText,
    };
}

export function AdminIdentityAttention() {
    useDocumentTitle('Identity attention center');
    const [activeTab, setActiveTab] = useState<AttentionTab>('users-without-teams');
    const [usersWithoutTeamsPage, setUsersWithoutTeamsPage] = useState(1);
    const [auth0Page, setAuth0Page] = useState(1);
    const [auth0ConflictPage, setAuth0ConflictPage] = useState(1);
    const [auth0SearchDraft, setAuth0SearchDraft] = useState('');
    const [auth0SearchQuery, setAuth0SearchQuery] = useState('');
    const [syncingSubjectId, setSyncingSubjectId] = useState<string | null>(null);
    const [approvingEmail, setApprovingEmail] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
    const [linkConfirm, setLinkConfirm] = useState<AdminIdentityAttentionAuth0EmailConflict | null>(
        null,
    );
    const [syncDbConfirm, setSyncDbConfirm] = useState<{
        subjectId: string;
        label: string;
    } | null>(null);
    const attentionParams = useMemo(() => {
        if (activeTab === 'auth0-missing-db') {
            return {
                query: auth0SearchQuery.trim() || undefined,
                page: auth0Page,
                pageSize: PAGE_SIZE,
            };
        }
        if (activeTab === 'auth0-email-conflicts') {
            return { page: auth0ConflictPage, pageSize: PAGE_SIZE };
        }
        return { page: usersWithoutTeamsPage, pageSize: PAGE_SIZE };
    }, [activeTab, auth0ConflictPage, auth0Page, auth0SearchQuery, usersWithoutTeamsPage]);
    const attentionQuery = useAdminIdentityAttention(attentionParams);
    const { approveAuth0IdentityGroupLink, syncAuth0UserToDb } =
        useAdminIdentityAttentionMutations();
    const usersWithoutTeams = attentionQuery.data?.usersWithoutTeams.items ?? [];
    const auth0OnlyUsers = attentionQuery.data?.auth0UsersMissingDbRows.items ?? [];
    const auth0EmailConflicts = attentionQuery.data?.auth0EmailConflicts.items ?? [];
    const usersWithoutTeamsPageData = attentionQuery.data?.usersWithoutTeams;
    const auth0PageData = attentionQuery.data?.auth0UsersMissingDbRows;
    const auth0EmailConflictsPageData = attentionQuery.data?.auth0EmailConflicts;
    const syncStats = attentionQuery.data?.syncStats;
    const attentionState = attentionQuery.data
        ? 'ready'
        : attentionQuery.isError
          ? 'error'
          : 'loading';

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setAuth0SearchQuery(auth0SearchDraft);
            setAuth0Page(1);
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [auth0SearchDraft]);

    const handleSyncToDb = async (subjectId: string) => {
        setStatusMessage(null);
        setSyncingSubjectId(subjectId);
        try {
            const result = await syncAuth0UserToDb.mutateAsync(subjectId);
            setStatusMessage(
                toStatusMessage(
                    result.success,
                    'Auth0 user synced to DB.',
                    'Sync was accepted, but no DB row was found yet.',
                ),
            );
        } catch (err) {
            setStatusMessage(toErrorStatusMessage(err, 'Could not sync Auth0 user. Try again.'));
        } finally {
            setSyncingSubjectId(null);
        }
    };

    const handleApproveIdentityGroupLink = async (
        conflict: AdminIdentityAttentionAuth0EmailConflict,
    ) => {
        setStatusMessage(null);
        setApprovingEmail(conflict.email);
        try {
            const result = await approveAuth0IdentityGroupLink.mutateAsync(conflict.email);
            setStatusMessage(
                toStatusMessage(
                    result.success,
                    `Linked ${result.linkedSubjectIds.length} Auth0 ${pluralize(
                        result.linkedSubjectIds.length,
                        'identity',
                        'identities',
                    )} for ${conflict.email}; DB subject is now ${result.primarySubjectId}.`,
                    `No matching DB user was found for ${conflict.email}.`,
                ),
            );
        } catch (err) {
            setStatusMessage(
                toErrorStatusMessage(err, 'Could not link these Auth0 identities. Try again.'),
            );
        } finally {
            setApprovingEmail(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <Link
                        to="/admin"
                        className="-my-0.5 inline-flex min-h-6 items-center gap-2 py-0.5 text-sm font-medium text-primary-500 hover:text-primary-600"
                    >
                        <ArrowLeft className="size-4" aria-hidden />
                        Back to admin
                    </Link>
                    <h1 className={cn('mt-2', pageTitleClassName)}>Identity attention center</h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Triage identity records that need manual review during Auth0 migration.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => attentionQuery.refetch()}
                    loading={attentionQuery.isFetching}
                    disabled={attentionQuery.isFetching}
                >
                    {attentionQuery.isFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
            </div>

            {attentionQuery.error && (
                <InlineAlert tone="error" title="Couldn't load identity attention data">
                    <p>Choose Refresh to try again.</p>
                    {attentionQuery.error instanceof Error ? (
                        <p className="mt-1 break-words">{attentionQuery.error.message}</p>
                    ) : null}
                </InlineAlert>
            )}

            {statusMessage && (
                <InlineAlert tone={statusMessage.tone}>{statusMessage.text}</InlineAlert>
            )}

            <TabList label="Attention sections" className={segmentedTabListClassName}>
                <SegmentedTab
                    id="attention-tab-sync-progress"
                    panelId="attention-panel"
                    selected={activeTab === 'sync-progress'}
                    onSelect={() => setActiveTab('sync-progress')}
                    icon={BarChart3}
                    label="Sync progress"
                />
                <SegmentedTab
                    id="attention-tab-users-without-teams"
                    panelId="attention-panel"
                    selected={activeTab === 'users-without-teams'}
                    onSelect={() => setActiveTab('users-without-teams')}
                    icon={Users}
                    label="Users without teams"
                />
                <SegmentedTab
                    id="attention-tab-auth0-missing-db"
                    panelId="attention-panel"
                    selected={activeTab === 'auth0-missing-db'}
                    onSelect={() => setActiveTab('auth0-missing-db')}
                    icon={Database}
                    label="Auth0 missing DB rows"
                />
                <SegmentedTab
                    id="attention-tab-auth0-email-conflicts"
                    panelId="attention-panel"
                    selected={activeTab === 'auth0-email-conflicts'}
                    onSelect={() => setActiveTab('auth0-email-conflicts')}
                    icon={Link2}
                    label="Same-email Auth0 identities"
                />
            </TabList>

            <div
                role="tabpanel"
                id="attention-panel"
                aria-labelledby={`attention-tab-${activeTab}`}
                className="space-y-6"
            >
                {activeTab === 'auth0-missing-db' && (
                    <div className="rounded-xl border bg-white p-4 shadow-sm">
                        <SearchField
                            inputClassName="admin-field-fill shadow-none"
                            value={auth0SearchDraft}
                            onValueChange={setAuth0SearchDraft}
                            placeholder="Search Auth0 users by email, name, or subject"
                            aria-label="Search Auth0 users by email, name, or subject"
                        />
                    </div>
                )}

                {activeTab === 'sync-progress' && (
                    <div className="space-y-4">
                        {attentionState === 'ready' ? (
                            <SyncProgressSection stats={syncStats} />
                        ) : attentionState === 'loading' ? (
                            <Card>
                                <LoadingState message="Loading identity attention data…" />
                            </Card>
                        ) : null}
                        <AdminAuth0BulkSyncHub />
                    </div>
                )}

                {activeTab !== 'sync-progress' && attentionState === 'loading' ? (
                    <Card>
                        <LoadingState message="Loading identity attention data…" />
                    </Card>
                ) : null}

                {activeTab === 'users-without-teams' && attentionState === 'ready' && (
                    <PaginatedAttentionSection
                        pageData={usersWithoutTeamsPageData}
                        page={usersWithoutTeamsPage}
                        onPageChange={setUsersWithoutTeamsPage}
                    >
                        <Card>
                            <CardContent className="space-y-4 p-5">
                                <SectionHeader
                                    title="Users without active teams"
                                    count={usersWithoutTeamsPageData?.total ?? 0}
                                    description="Oldest users appear first so admins can resolve migration leftovers in order."
                                />
                                <div className="-mx-5">
                                    <Table stickyHeader className="min-w-[40rem]">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User</TableHead>
                                                <TableHead>Subject</TableHead>
                                                <TableHead>Created</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {usersWithoutTeams.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={3}
                                                        className="text-sm text-neutral-500"
                                                    >
                                                        No active DB users without teams.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                usersWithoutTeams.map((user) => (
                                                    <TableRow key={user.id}>
                                                        <TableCell>
                                                            <div className="font-medium text-neutral-950">
                                                                {user.name}
                                                            </div>
                                                            <div className="text-xs text-neutral-500">
                                                                {user.email}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-sm text-neutral-600">
                                                            {user.subjectId ?? 'Missing'}
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap text-sm text-neutral-600">
                                                            {new Date(
                                                                user.dateCreated,
                                                            ).toLocaleString()}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </PaginatedAttentionSection>
                )}

                {activeTab === 'auth0-missing-db' && attentionState === 'ready' && (
                    <PaginatedAttentionSection
                        pageData={auth0PageData}
                        page={auth0Page}
                        onPageChange={setAuth0Page}
                    >
                        <Card>
                            <CardContent className="space-y-4 p-5">
                                <SectionHeader
                                    title="Auth0 users missing DB rows"
                                    count={auth0PageData?.total ?? 0}
                                    description="Auth0 users that do not match a DB user by subject or email."
                                />
                                <div className="-mx-5">
                                    <Table stickyHeader className="min-w-[40rem]">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User</TableHead>
                                                <TableHead>Subject</TableHead>
                                                <TableHead>Created</TableHead>
                                                <TableHead className="text-right">Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {auth0OnlyUsers.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={4}
                                                        className="text-sm text-neutral-500"
                                                    >
                                                        No Auth0 users are missing DB rows.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                auth0OnlyUsers.map((user) => (
                                                    <TableRow key={user.subjectId || user.email}>
                                                        <TableCell>
                                                            <div className="font-medium text-neutral-950">
                                                                {user.name || user.email}
                                                            </div>
                                                            <div className="text-xs text-neutral-500">
                                                                {user.email || 'No email'}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-sm text-neutral-600">
                                                            {user.subjectId}
                                                        </TableCell>
                                                        <TableCell className="whitespace-nowrap text-sm text-neutral-600">
                                                            {user.createdAt
                                                                ? new Date(
                                                                      user.createdAt,
                                                                  ).toLocaleString()
                                                                : 'Unknown'}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button
                                                                variant="destructiveOutline"
                                                                size="sm"
                                                                loading={
                                                                    syncingSubjectId ===
                                                                    user.subjectId
                                                                }
                                                                onClick={() =>
                                                                    setSyncDbConfirm({
                                                                        subjectId: user.subjectId,
                                                                        label:
                                                                            user.email ||
                                                                            user.subjectId,
                                                                    })
                                                                }
                                                                disabled={
                                                                    !user.subjectId ||
                                                                    syncingSubjectId ===
                                                                        user.subjectId
                                                                }
                                                                aria-label={`${
                                                                    syncingSubjectId ===
                                                                    user.subjectId
                                                                        ? 'Syncing…'
                                                                        : 'Sync to DB'
                                                                } for ${user.email || user.subjectId}`}
                                                            >
                                                                {syncingSubjectId === user.subjectId
                                                                    ? 'Syncing…'
                                                                    : 'Sync to DB'}
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </PaginatedAttentionSection>
                )}

                {activeTab === 'auth0-email-conflicts' && attentionState === 'ready' && (
                    <PaginatedAttentionSection
                        pageData={auth0EmailConflictsPageData}
                        page={auth0ConflictPage}
                        onPageChange={setAuth0ConflictPage}
                    >
                        <Card>
                            <CardContent className="space-y-4 p-5">
                                <SectionHeader
                                    title="Auth0 same-email identity conflicts"
                                    count={auth0EmailConflictsPageData?.total ?? 0}
                                    description="Auth0 identities grouped by email. Approve linking the accounts together before retrying sync."
                                />
                                <div className="-mx-5">
                                    <Table stickyHeader className="min-w-[40rem]">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Email</TableHead>
                                                <TableHead>DB user</TableHead>
                                                <TableHead>Auth0 identities</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {auth0EmailConflicts.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={3}
                                                        className="text-sm text-neutral-500"
                                                    >
                                                        No same-email Auth0 identity conflicts
                                                        found.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                auth0EmailConflicts.map((conflict) => (
                                                    <TableRow key={conflict.email}>
                                                        <TableCell className="align-top">
                                                            <div className="font-medium text-neutral-950">
                                                                {conflict.email}
                                                            </div>
                                                            <div className="text-xs text-neutral-500">
                                                                {conflict.identities.length} Auth0{' '}
                                                                {pluralize(
                                                                    conflict.identities.length,
                                                                    'identity',
                                                                    'identities',
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="align-top text-sm text-neutral-600">
                                                            {conflict.dbUser ? (
                                                                <div>
                                                                    <div className="font-medium text-neutral-950">
                                                                        {conflict.dbUser.name}
                                                                    </div>
                                                                    <div className="text-xs text-neutral-500">
                                                                        Current subject:{' '}
                                                                        {conflict.dbUser
                                                                            .subjectId ?? 'Missing'}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                'No DB user with this email'
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="space-y-2">
                                                                {conflict.identities.map(
                                                                    (identity) => (
                                                                        <div
                                                                            key={identity.subjectId}
                                                                            className="rounded-lg border border-neutral-200 p-3"
                                                                        >
                                                                            <div>
                                                                                <div className="font-medium text-neutral-950">
                                                                                    {identity.name ||
                                                                                        identity.email}
                                                                                </div>
                                                                                <div className="text-xs text-neutral-500">
                                                                                    {
                                                                                        identity.subjectId
                                                                                    }
                                                                                </div>
                                                                                <div className="text-xs text-neutral-500">
                                                                                    {identity.connection ??
                                                                                        identity.provider ??
                                                                                        'Unknown connection'}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ),
                                                                )}
                                                                <div className="flex justify-end">
                                                                    <Button
                                                                        variant="destructiveOutline"
                                                                        size="sm"
                                                                        loading={
                                                                            approvingEmail ===
                                                                            conflict.email
                                                                        }
                                                                        onClick={() =>
                                                                            setLinkConfirm(conflict)
                                                                        }
                                                                        disabled={
                                                                            !conflict.dbUser ||
                                                                            approvingEmail ===
                                                                                conflict.email
                                                                        }
                                                                        aria-label={`${
                                                                            approvingEmail ===
                                                                            conflict.email
                                                                                ? 'Linking…'
                                                                                : 'Approve account link'
                                                                        } for ${conflict.email}`}
                                                                    >
                                                                        {approvingEmail ===
                                                                        conflict.email
                                                                            ? 'Linking…'
                                                                            : 'Approve account link'}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </PaginatedAttentionSection>
                )}
            </div>
            <ConfirmDialog
                open={syncDbConfirm !== null}
                title={`Sync ${syncDbConfirm?.label ?? ''} to the database?`}
                confirmLabel="Sync to DB"
                pendingLabel="Syncing…"
                destructive
                onCancel={() => setSyncDbConfirm(null)}
                onConfirm={async () => {
                    const target = syncDbConfirm;
                    if (!target) return;
                    await handleSyncToDb(target.subjectId);
                    setSyncDbConfirm(null);
                }}
            />
            <ConfirmDialog
                open={linkConfirm !== null}
                title={`Approve account link for ${linkConfirm?.email ?? ''}?`}
                description={
                    linkConfirm
                        ? `This links the ${linkConfirm.identities.length} Auth0 ${pluralize(
                              linkConfirm.identities.length,
                              'identity',
                              'identities',
                          )} for this email together.`
                        : undefined
                }
                confirmLabel="Approve account link"
                pendingLabel="Linking…"
                destructive
                onCancel={() => setLinkConfirm(null)}
                onConfirm={async () => {
                    const conflict = linkConfirm;
                    if (conflict) {
                        await handleApproveIdentityGroupLink(conflict);
                    }
                    setLinkConfirm(null);
                }}
            />
        </div>
    );
}

function percent(part: number, total: number): number {
    if (total <= 0) {
        return 0;
    }
    return Math.round((part / total) * 100);
}

function SyncProgressSection({ stats }: { stats?: AdminIdentitySyncStats }) {
    const companyPercent = percent(stats?.companiesSynced ?? 0, stats?.activeCompaniesTotal ?? 0);
    const userPercent = percent(stats?.usersSynced ?? 0, stats?.activeUsersTotal ?? 0);

    return (
        <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
                <SyncStatCard
                    label="Company org coverage"
                    value={`${companyPercent}%`}
                    detail={`${stats?.companiesSynced ?? 0} of ${stats?.activeCompaniesTotal ?? 0} active companies have Auth0 org IDs`}
                />
                <SyncStatCard
                    label="User subject coverage"
                    value={`${userPercent}%`}
                    detail={`${stats?.usersSynced ?? 0} of ${stats?.activeUsersTotal ?? 0} active users have Auth0 subject IDs`}
                />
                <SyncStatCard
                    label="Unsynced team users"
                    value={String(stats?.unsyncedActiveCompanyUsers ?? 0)}
                    detail="Active users on active company teams without Auth0 subject IDs"
                />
            </div>
            <Card>
                <CardContent className="space-y-2 p-4 text-sm text-neutral-600">
                    <h2 className={sectionTitleClassName}>DB-backed sync rollout summary</h2>
                    <p className="max-w-prose">
                        These counts come from the WEVO database only. They intentionally avoid live
                        Auth0 sampling so this dashboard stays fast and avoids Management API rate
                        limit surprises.
                    </p>
                    <p className="max-w-prose">
                        Same-email identities across Auth0 connections appear in their own tab for
                        manual account-link approval. DB email duplicate counts are not shown
                        because WEVO enforces unique user emails in the database.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

function SyncStatCard({ detail, label, value }: { detail: string; label: string; value: string }) {
    return (
        <Card>
            <CardContent className="space-y-2 p-4">
                <div className="text-sm font-medium text-neutral-500">{label}</div>
                <div className="text-2xl font-semibold leading-tight tabular-nums text-neutral-950">
                    {value}
                </div>
                <div className="text-sm text-neutral-500">{detail}</div>
            </CardContent>
        </Card>
    );
}

function PaginatedAttentionSection({
    children,
    onPageChange,
    page,
    pageData,
}: {
    children: JSX.Element;
    onPageChange: (page: number) => void;
    page: number;
    pageData?: { items: unknown[]; pageSize: number; total: number; totalPages: number };
}) {
    return (
        <AdminDashboardPaginatedSection
            rows={pageData?.items ?? []}
            page={page}
            pageSize={pageData?.pageSize ?? PAGE_SIZE}
            total={pageData?.total ?? 0}
            totalPages={pageData?.totalPages ?? 1}
            onPageChange={onPageChange}
            render={() => children}
        />
    );
}

function SectionHeader({
    count,
    description,
    title,
}: {
    count: number;
    description: string;
    title: string;
}) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <h2 className={cn('flex items-center gap-2', sectionTitleClassName)}>
                    {count > 0 ? (
                        <AlertTriangle className="size-5 shrink-0 text-amber-600" aria-hidden />
                    ) : null}
                    {title}
                </h2>
                <p className="mt-1 max-w-prose text-sm text-neutral-500">{description}</p>
            </div>
            <span className="shrink-0 rounded-full bg-neutral-100 px-3 py-1 text-sm font-semibold tabular-nums text-neutral-700">
                {count}
            </span>
        </div>
    );
}
